/* global globalThis */
// Independent per-user ledger. Amounts are stored as decimal strings and summed in cents.
const MAX_AMOUNT = 9999999999.99;
const COLLECTIONS = { accounts: 200, transactions: 50000, budgets: 2000, import_batches: 1000 };
const ACCOUNT_TYPES = ['cash', 'bank', 'credit', 'ewallet', 'other'];
const TRANSACTION_TYPES = ['income', 'expense', 'transfer', 'adjustment'];
const clone = value => JSON.parse(JSON.stringify(value));
export const emptyLedger = () => ({ accounts: [], transactions: [], budgets: [], import_batches: [] });
export const makeId = () => globalThis.crypto?.randomUUID?.() || `ledger-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

export function parseMoney(value) {
  if (typeof value !== 'string' && typeof value !== 'number') return NaN;
  const text = String(value).trim();
  if (!/^[+-]?\d+(?:\.\d{1,2})?$/.test(text)) return NaN;
  const amount = Number(text);
  return Number.isFinite(amount) && Math.abs(amount) <= MAX_AMOUNT ? amount : NaN;
}
const cents = value => Math.round(parseMoney(value) * 100);
export function moneyString(value) {
  const amount = parseMoney(value);
  return Number.isFinite(amount) ? (Math.round(amount * 100) / 100).toFixed(2) : '';
}
export function formatLedgerMoney(value, currency = 'TWD') {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('zh-TW', { style: 'currency', currency: /^[A-Z]{3}$/.test(currency) ? currency : 'TWD', maximumFractionDigits: 2 }).format(amount);
}
function dateParts(value) {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,6})?)?(Z|[+-]\d{2}:\d{2}))?$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second, zone] = match;
  if (+year < 1 || +month < 1 || +month > 12 || +day < 1) return null;
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.getUTCDate() !== +day || date.getUTCMonth() !== +month - 1) return null;
  if (hour !== undefined && (+hour > 23 || +minute > 59 || +(second || 0) > 59)) return null;
  if (zone && zone !== 'Z' && (+zone.slice(1, 3) > 23 || +zone.slice(4) > 59)) return null;
  return match;
}
function localDate(value) {
  if (!dateParts(value)) return '';
  if (value.length === 10) return value;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
const validMonth = value => typeof value === 'string' && /^\d{4}-(?:0[1-9]|1[0-2])$/.test(value) && Number(value.slice(0, 4)) >= 1;

export function validateLedger(data) {
  const errors = [];
  if (!data || typeof data !== 'object' || Array.isArray(data)) return ['帳本必須是完整資料物件。'];
  for (const [key, limit] of Object.entries(COLLECTIONS)) {
    if (!Array.isArray(data[key])) errors.push(`帳本缺少 ${key} 清單。`);
    else if (data[key].length > limit) errors.push(`${key} 超過 ${limit} 筆上限。`);
  }
  if (errors.length) return errors;
  const text = (value, max, required = false) => typeof value === 'string' && value.length <= max && (!required || !!value.trim());
  const field = (row, key, max, label, required = false) => {
    if (row[key] === undefined && !required) return;
    if (!text(row[key], max, required)) errors.push(`${label}的 ${key} 格式或長度有誤。`);
  };
  for (const key of Object.keys(COLLECTIONS)) {
    const ids = new Set();
    data[key].forEach((row, index) => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) { errors.push(`${key} 第 ${index + 1} 筆格式錯誤。`); return; }
      field(row, 'id', 80, `${key} 第 ${index + 1} 筆`, true);
      if (ids.has(row.id)) errors.push(`${key} 有重複識別碼。`);
      ids.add(row.id);
    });
  }
  if (errors.length) return errors;
  const accountMap = new Map(data.accounts.map(row => [row.id, row]));
  data.accounts.forEach((row, index) => {
    const label = `第 ${index + 1} 個帳戶`;
    field(row, 'name', 255, label, true); field(row, 'note', 2000, label);
    if (!ACCOUNT_TYPES.includes(row.type)) errors.push(`${label}的帳戶類型無效。`);
    if (!/^[A-Z]{3}$/.test(row.currency || '')) errors.push(`${label}請使用三碼大寫幣別。`);
    if (!Number.isFinite(parseMoney(row.opening_balance))) errors.push(`${label}的期初餘額必須是最多兩位小數的有效金額。`);
    if (typeof row.archived !== 'boolean') errors.push(`${label}的封存狀態無效。`);
  });
  data.transactions.forEach((row, index) => {
    const label = `第 ${index + 1} 筆交易`, source = accountMap.get(row.account_id), target = accountMap.get(row.to_account_id);
    if (!TRANSACTION_TYPES.includes(row.type)) errors.push(`${label}的交易類型無效。`);
    if (!source) errors.push(`${label}找不到交易帳戶。`);
    if (!Number.isFinite(parseMoney(row.amount)) || (row.type !== 'adjustment' && parseMoney(row.amount) <= 0)) errors.push(`${label}金額無效；收入、支出及轉帳須大於零，最多兩位小數。`);
    if (!localDate(row.date)) errors.push(`${label}日期無效，請填有效日期或含時區的時間。`);
    if (row.type === 'transfer') {
      if (!target || row.account_id === row.to_account_id) errors.push(`${label}請選擇不同的轉入帳戶。`);
      else if (source && source.currency !== target.currency) errors.push(`${label}目前只支援同幣別轉帳。`);
    } else if (row.to_account_id) errors.push(`${label}只有轉帳能指定轉入帳戶。`);
    for (const [key, max] of Object.entries({ category: 120, subcategory: 120, name: 255, note: 5000, project: 120, source_id: 200 })) field(row, key, max, label);
    if (row.tags !== undefined && (!Array.isArray(row.tags) || row.tags.length > 30 || row.tags.some(tag => !text(tag, 80, true)))) errors.push(`${label}標籤格式或數量有誤。`);
  });
  const budgetKeys = new Set();
  data.budgets.forEach((row, index) => {
    const label = `第 ${index + 1} 筆預算`;
    field(row, 'category', 120, label, true);
    if (!validMonth(row.month) || !/^[A-Z]{3}$/.test(row.currency || '')) errors.push(`${label}月份或幣別無效。`);
    if (!(parseMoney(row.amount) > 0)) errors.push(`${label}額度必須大於零，最多兩位小數。`);
    const key = JSON.stringify([row.month, row.currency, row.category]);
    if (budgetKeys.has(key)) errors.push(`${label}與相同月份、幣別的分類預算重複。`);
    budgetKeys.add(key);
  });
  data.import_batches.forEach((row, index) => {
    const label = `第 ${index + 1} 份匯入紀錄`;
    field(row, 'filename', 255, label, true);
    if (!localDate(row.imported_at) || !Number.isInteger(row.count) || row.count < 0 || row.count > 50000) errors.push(`${label}日期或筆數無效。`);
  });
  return errors;
}

function balanceCents(data, throughDate) {
  const balances = Object.fromEntries(data.accounts.map(account => [account.id, cents(account.opening_balance)]));
  data.transactions.forEach(tx => {
    if (throughDate && localDate(tx.date) > throughDate) return;
    const amount = cents(tx.amount);
    if (Object.prototype.hasOwnProperty.call(balances, tx.account_id)) balances[tx.account_id] += tx.type === 'expense' || tx.type === 'transfer' ? -amount : amount;
    if (tx.type === 'transfer' && Object.prototype.hasOwnProperty.call(balances, tx.to_account_id)) balances[tx.to_account_id] += amount;
  });
  return balances;
}
export function accountBalances(data, { throughDate } = {}) {
  return Object.fromEntries(Object.entries(balanceCents(data, throughDate && localDate(throughDate))).map(([id, value]) => [id, value / 100]));
}
export function currencyTotals(data) {
  const balances = balanceCents(data), totals = new Map();
  data.accounts.forEach(account => {
    const total = totals.get(account.currency) || { currency: account.currency, assets: 0, debt: 0, net: 0 };
    const balance = balances[account.id];
    total.assets += Math.max(0, balance); total.debt += Math.max(0, -balance); total.net += balance;
    totals.set(account.currency, total);
  });
  return [...totals.values()].map(total => ({ ...total, assets: total.assets / 100, debt: total.debt / 100, net: total.net / 100 }));
}
export function monthlySummary(data, { month, accountId, currency } = {}) {
  const empty = { income: 0, expense: 0, adjustment: 0, transfer: 0, count: 0, net: 0, categories: [], daily: [], transactions: [] };
  if (!validMonth(month)) return empty;
  const filteredAccount = accountId && accountId !== 'all' ? accountId : null;
  const relevant = new Set(data.accounts.filter(account => (!currency || account.currency === currency) && (!filteredAccount || account.id === filteredAccount)).map(account => account.id));
  const transactions = data.transactions.filter(tx => localDate(tx.date).slice(0, 7) === month && (relevant.has(tx.account_id) || (tx.type === 'transfer' && relevant.has(tx.to_account_id))));
  const totals = { income: 0, expense: 0, adjustment: 0, transfer: 0 }, categories = new Map(), changes = new Map();
  transactions.forEach(tx => {
    const amount = cents(tx.amount), day = localDate(tx.date);
    totals[tx.type] += amount;
    if (tx.type === 'expense') categories.set(tx.category || '未分類', (categories.get(tx.category || '未分類') || 0) + amount);
    const change = changes.get(day) || { balance: 0, income: 0, expense: 0 };
    if (relevant.has(tx.account_id)) change.balance += tx.type === 'expense' || tx.type === 'transfer' ? -amount : amount;
    if (tx.type === 'transfer' && relevant.has(tx.to_account_id)) change.balance += amount;
    if (tx.type === 'income' || tx.type === 'expense') change[tx.type] += amount;
    changes.set(day, change);
  });
  const first = `${month}-01`, prior = new Date(new Date(`${first}T00:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10);
  const before = balanceCents(data, prior);
  let balance = [...relevant].reduce((total, id) => total + before[id], 0);
  const days = new Date(Date.UTC(+month.slice(0, 4), +month.slice(5), 0)).getUTCDate();
  const daily = Array.from({ length: days }, (_, index) => {
    const date = `${month}-${String(index + 1).padStart(2, '0')}`, change = changes.get(date) || { balance: 0, income: 0, expense: 0 };
    balance += change.balance;
    return { date, balance: balance / 100, income: change.income / 100, expense: change.expense / 100 };
  });
  return { ...Object.fromEntries(Object.entries(totals).map(([key, value]) => [key, value / 100])), count: transactions.length, net: (totals.income - totals.expense) / 100, categories: [...categories].map(([category, amount]) => ({ category, amount: amount / 100 })).sort((a, b) => b.amount - a.amount), daily, transactions };
}

// SHA-256 is deliberately local: CSV contents never leave the browser during preview.
function fingerprintText(text) {
  const bytes = unescape(encodeURIComponent(text));
  const words = [], constants = [], initial = [];
  for (let candidate = 2; constants.length < 64; candidate += 1) {
    let prime = true;
    for (let divisor = 2; divisor * divisor <= candidate; divisor += 1) if (candidate % divisor === 0) { prime = false; break; }
    if (prime) { if (initial.length < 8) initial.push((Math.sqrt(candidate) * 4294967296) | 0); constants.push((Math.cbrt(candidate) * 4294967296) | 0); }
  }
  for (let i = 0; i < bytes.length; i += 1) words[i >> 2] = (words[i >> 2] || 0) | (bytes.charCodeAt(i) << (24 - i % 4 * 8));
  words[bytes.length >> 2] = (words[bytes.length >> 2] || 0) | (0x80 << (24 - bytes.length % 4 * 8));
  const length = Math.ceil((bytes.length + 9) / 64) * 16;
  words[length - 2] = Math.floor(bytes.length * 8 / 4294967296); words[length - 1] = bytes.length * 8;
  const hash = [...initial], rotate = (x, by) => (x >>> by) | (x << (32 - by));
  for (let offset = 0; offset < length; offset += 16) {
    const schedule = Array.from({ length: 64 }, (_, i) => words[offset + i] | 0);
    let [a, b, c, d, e, f, g, h] = hash;
    for (let i = 0; i < 64; i += 1) {
      if (i >= 16) { const x = schedule[i - 15], y = schedule[i - 2]; schedule[i] = (schedule[i - 16] + (rotate(x, 7) ^ rotate(x, 18) ^ (x >>> 3)) + schedule[i - 7] + (rotate(y, 17) ^ rotate(y, 19) ^ (y >>> 10))) | 0; }
      const t1 = (h + (rotate(e, 6) ^ rotate(e, 11) ^ rotate(e, 25)) + ((e & f) ^ (~e & g)) + constants[i] + schedule[i]) | 0;
      const t2 = ((rotate(a, 2) ^ rotate(a, 13) ^ rotate(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    [a, b, c, d, e, f, g, h].forEach((value, i) => { hash[i] = (hash[i] + value) | 0; });
  }
  return `csv-${hash.map(value => (value >>> 0).toString(16).padStart(8, '0')).join('')}`;
}
function readCsv(text) {
  const rows = []; let row = [], cell = '', quoted = false, afterQuote = false, line = 1, startLine = 1;
  const pushCell = () => { row.push(cell); cell = ''; afterQuote = false; };
  const pushRow = () => { pushCell(); if (row.some(value => value !== '')) rows.push({ values: row, line: startLine }); row = []; startLine = line + 1; };
  const input = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (quoted) {
      if (char === '"') { if (input[i + 1] === '"') { cell += '"'; i += 1; } else { quoted = false; afterQuote = true; } }
      else { cell += char; if (char === '\n') line += 1; }
    } else if (char === '"') {
      if (cell || afterQuote) throw new Error(`第 ${line} 行引號格式有誤。`);
      quoted = true;
    } else if (char === ',') pushCell();
    else if (char === '\n' || char === '\r') { if (char === '\r' && input[i + 1] === '\n') i += 1; pushRow(); line += 1; }
    else { if (afterQuote && !/\s/.test(char)) throw new Error(`第 ${line} 行引號後有多餘內容。`); if (!afterQuote) cell += char; }
  }
  if (quoted) throw new Error(`第 ${startLine} 行引號未結束。`);
  if (cell || row.length || afterQuote) pushRow();
  return rows;
}
const OWN_HEADERS = ['record_id', 'type', 'account', 'account_id', 'currency', 'to_account', 'to_account_id', 'amount', 'date', 'category', 'subcategory', 'name', 'note', 'project', 'tags', 'source_id'];
const MOZE_HEADER_ALIASES = new Map([
  ['Account', '帳戶'], ['Currency', '幣種'], ['Type', '記錄類型'],
  ['Main Category', '主類別'], ['Subcategory', '子類別'], ['Price', '金額'],
  ['Fee', '手續費'], ['Discount', '折扣'], ['Name', '名稱'], ['Store', '商家'],
  ['Date', '日期'], ['Time', '時間'], ['Project', '專案'], ['Note', '描述'],
  ['Tags', '標籤'], ['Target', '對象'],
]);
const safeCell = value => { const text = String(value ?? ''); return /^[=+@\-\t\r']/.test(text) ? `'${text}` : text; };
const unescapeCell = value => /^'[=+@\-\t\r']/.test(value) ? value.slice(1) : value;
const csvCell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
function importAmount(text) {
  const value = String(text || '').trim();
  return parseMoney(/^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?$/.test(value) ? value.replace(/,/g, '') : value);
}
function importDate(date, time) {
  const day = date.trim().replace(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/, (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`);
  if (!time?.trim()) return day;
  const clock = time.trim();
  return `${day}T${/^\d{2}:\d{2}$/.test(clock) ? `${clock}:00` : clock}+08:00`;
}
export function previewCsvImport(text, current, { filename = 'import.csv' } = {}) {
  const result = { data: null, accounts: [], transactions: [], errors: [], warnings: [], duplicate: false, fingerprint: '', count: 0 };
  if (typeof text !== 'string' || !text.trim()) { result.errors.push('CSV 是空白檔案。'); return result; }
  const existingErrors = validateLedger(current);
  if (existingErrors.length) { result.errors.push('目前帳本格式有誤，請先確認帳本。'); return result; }
  let rows;
  try { rows = readCsv(text); } catch (error) { result.errors.push(error.message); return result; }
  if (!rows.length) { result.errors.push('CSV 缺少欄位標題。'); return result; }
  result.fingerprint = fingerprintText(JSON.stringify(rows.map(row => row.values)));
  if (current.import_batches.some(batch => batch.id === result.fingerprint)) { result.duplicate = true; result.warnings.push('同一份 CSV 已匯入過。'); return result; }
  const rawHeaders = rows.shift().values.map(header => header.trim());
  const own = OWN_HEADERS.every(header => rawHeaders.includes(header));
  const headers = own ? rawHeaders : rawHeaders.map(header => MOZE_HEADER_ALIASES.get(header) || header);
  if (new Set(headers).size !== headers.length) { result.errors.push('CSV 欄位標題重複。'); return result; }
  const required = own ? OWN_HEADERS : ['帳戶', '幣種', '記錄類型', '金額', '日期'];
  const missing = required.filter(header => !headers.includes(header));
  if (missing.length) { result.errors.push(`缺少 CSV 欄位：${missing.join('、')}。請使用 MOZE 原始匯出或本站匯出格式。`); return result; }
  const knownHeaders = own ? OWN_HEADERS : ['帳戶', '幣種', '記錄類型', '主類別', '子類別', '金額', '手續費', '折扣', '名稱', '商家', '日期', '時間', '專案', '描述', '標籤', '對象'];
  const extraHeaders = headers.filter(header => !knownHeaders.includes(header));
  const data = clone(current), imported = [], pendingTransfers = [];
  const getAccount = (name, currency, line, originalId) => {
    if (!name.trim() || !/^[A-Z]{3}$/.test(currency)) { result.errors.push(`第 ${line} 行帳戶名稱或幣別無效。`); return null; }
    if (originalId !== undefined) {
      if (!originalId.trim() || originalId.length > 80) { result.errors.push(`第 ${line} 行帳戶識別碼無效。`); return null; }
      const matchedId = data.accounts.find(account => account.id === originalId);
      if (matchedId) {
        if (matchedId.name !== name || matchedId.currency !== currency) { result.errors.push(`第 ${line} 行帳戶識別碼與現有帳戶名稱或幣別衝突。`); return null; }
        return matchedId;
      }
    }
    const matches = originalId === undefined ? data.accounts.filter(account => account.name === name && account.currency === currency) : [];
    if (matches.length > 1) { result.errors.push(`第 ${line} 行有多個同名同幣別帳戶，請先為帳戶設定不同名稱。`); return null; }
    if (matches.length) return matches[0];
    const account = { id: originalId || makeId(), name, type: 'cash', currency, opening_balance: '0.00', archived: false, note: '' };
    data.accounts.push(account); result.accounts.push(account); return account;
  };
  rows.forEach(({ values, line }) => {
    if (values.length !== headers.length) { result.errors.push(`第 ${line} 行有 ${values.length} 欄，應為 ${headers.length} 欄。`); return; }
    const row = Object.fromEntries(headers.map((header, i) => [header, own ? unescapeCell(values[i]) : values[i]]));
    if (extraHeaders.some(header => row[header]?.trim())) { result.errors.push(`第 ${line} 行含尚未支援的欄位 ${extraHeaders.filter(header => row[header]?.trim()).join('、')}，請先確認欄位內容。`); return; }
    const amount = importAmount(own ? row.amount : row['金額']);
    const date = own ? row.date : importDate(row['日期'], row['時間']);
    if (!Number.isFinite(amount)) { result.errors.push(`第 ${line} 行金額無效，最多兩位小數。`); return; }
    if (!localDate(date)) { result.errors.push(`第 ${line} 行日期或時間無效。`); return; }
    const currency = (own ? row.currency : row['幣種']).trim().toUpperCase();
    const account = getAccount(own ? row.account : row['帳戶'], currency, line, own ? row.account_id : undefined);
    if (!account) return;
    if (!own) {
      for (const key of ['手續費', '折扣']) if (row[key]?.trim() && importAmount(row[key]) !== 0) result.errors.push(`第 ${line} 行含${key}，目前無法可靠還原，請保留原始 CSV 並先處理此筆。`);
    }
    const label = (own ? row.type : row['記錄類型']).trim().toLowerCase();
    const kind = own ? label : ({ '收入': 'income', '支出': 'expense', '餘額調整': 'adjustment', '调整': 'adjustment', '調整': 'adjustment', '余额调整': 'adjustment', 'income': 'income', 'expense': 'expense', 'balance adjustment': 'adjustment', 'adjustment': 'adjustment', '轉出': 'out', '转出': 'out', '轉入': 'in', '转入': 'in', 'transfer out': 'out', 'transfer in': 'in' })[label];
    if (!kind || !(own ? TRANSACTION_TYPES : [...TRANSACTION_TYPES, 'in', 'out']).includes(kind)) { result.errors.push(`第 ${line} 行的「${label}」尚未支援，為避免漏帳，本次不會匯入。`); return; }
    if ((kind !== 'adjustment' && amount === 0) || ((kind === 'income' || kind === 'in') && amount < 0) || (own && kind !== 'adjustment' && amount < 0) || (!own && (kind === 'expense' || kind === 'out') && amount > 0)) { result.errors.push(`第 ${line} 行金額正負號與交易類型不符。`); return; }
    let tags;
    try { tags = own ? JSON.parse(row.tags || '[]') : (row['標籤'] || '').split(/[,;，；]/).map(tag => tag.trim()).filter(Boolean); }
    catch { result.errors.push(`第 ${line} 行標籤格式無效。`); return; }
    const metadata = !own ? [['商家', row['商家']], ['對象', row['對象']]].filter(([, value]) => value).map(([key, value]) => `${key}：${value}`) : [];
    const tx = { id: own ? row.record_id : makeId(), type: kind, account_id: account.id, amount: moneyString(kind === 'adjustment' ? amount : Math.abs(amount)), date, category: own ? row.category : row['主類別'] || '', subcategory: own ? row.subcategory : row['子類別'] || '', name: own ? row.name : row['名稱'] || '', note: own ? row.note : [row['描述'] || '', ...metadata].filter(Boolean).join('\n'), project: own ? row.project : row['專案'] || '', tags, source_id: own ? row.source_id || row.record_id : `${result.fingerprint}:${line}` };
    if (kind === 'out' || kind === 'in') { pendingTransfers.push({ tx, line, direction: kind, currency }); return; }
    if (kind === 'transfer') {
      const target = getAccount(row.to_account, currency, line, row.to_account_id);
      if (!target) return;
      tx.to_account_id = target.id;
    }
    imported.push(tx);
  });
  const used = new Set();
  pendingTransfers.forEach((entry, index) => {
    if (used.has(index)) return;
    const candidates = pendingTransfers.map((other, otherIndex) => ({ ...other, index: otherIndex })).filter(other => !used.has(other.index) && other.index !== index && other.direction !== entry.direction && other.tx.date === entry.tx.date && other.currency === entry.currency && other.tx.amount === entry.tx.amount && other.tx.account_id !== entry.tx.account_id);
    if (candidates.length !== 1) { result.errors.push(`第 ${entry.line} 行轉帳找不到唯一、同幣別同金額的對應記錄；請先核對轉入與轉出資料。`); return; }
    const other = candidates[0], reverse = pendingTransfers.filter((candidate, i) => !used.has(i) && i !== other.index && candidate.direction !== other.direction && candidate.tx.date === other.tx.date && candidate.currency === other.currency && candidate.tx.amount === other.tx.amount && candidate.tx.account_id !== other.tx.account_id);
    if (reverse.length !== 1) { result.errors.push(`第 ${entry.line} 行同時有多筆相同轉帳，無法可靠配對。`); return; }
    used.add(index); used.add(other.index);
    const out = entry.direction === 'out' ? entry : other, incoming = entry.direction === 'in' ? entry : other;
    imported.push({ ...out.tx, type: 'transfer', to_account_id: incoming.tx.account_id, name: out.tx.name || incoming.tx.name || '轉帳', note: [...new Set([out.tx.note, incoming.tx.note].filter(Boolean))].join('\n'), tags: [...new Set([...out.tx.tags, ...incoming.tx.tags])] });
  });
  result.count = imported.length; result.transactions = imported;
  data.transactions.push(...imported);
  if (imported.length === 0 && !result.errors.length) result.errors.push('CSV 沒有可匯入的交易。');
  data.import_batches.push({ id: result.fingerprint, filename: filename.slice(0, 255), imported_at: new Date().toISOString(), count: imported.length });
  result.errors.push(...validateLedger(data));
  if (result.accounts.length) result.warnings.push(`新增 ${result.accounts.length} 個帳戶，期初餘額暫設為 0、類型暫設為現金；請依原始帳本確認帳戶類型，並補入記帳前餘額。`);
  if (!own) result.warnings.push('重複檢查只辨識整份相同檔案；不同檔案若匯出日期重疊，仍可能重複記帳。請選不重疊的日期範圍，並在儲存前核對交易。');
  result.warnings.push('CSV 只匯入交易文字，無法還原圖片、分期、循環事件或應收應付關聯；完整備份請使用 JSON。');
  if (!result.errors.length) result.data = data;
  return result;
}

export function exportLedgerCsv(data) {
  const accountMap = new Map(data.accounts.map(account => [account.id, account]));
  const rows = data.transactions.map(tx => ({ record_id: tx.id, type: tx.type, account: accountMap.get(tx.account_id)?.name || '', account_id: tx.account_id, currency: accountMap.get(tx.account_id)?.currency || '', to_account: tx.type === 'transfer' ? accountMap.get(tx.to_account_id)?.name || '' : '', to_account_id: tx.to_account_id || '', amount: moneyString(tx.amount), date: tx.date, category: tx.category || '', subcategory: tx.subcategory || '', name: tx.name || '', note: tx.note || '', project: tx.project || '', tags: JSON.stringify(tx.tags || []), source_id: tx.source_id || '' }));
  return `\uFEFF${[OWN_HEADERS.map(csvCell).join(','), ...rows.map(row => OWN_HEADERS.map(key => csvCell(key === 'amount' ? row[key] : safeCell(row[key]))).join(','))].join('\r\n')}\r\n`;
}
export function exportLedgerJson(data) {
  return JSON.stringify({ format: 'finance-ledger', version: 1, exported_at: new Date().toISOString(), data }, null, 2);
}
export function parseLedgerBackup(text) {
  try {
    const backup = JSON.parse(text.replace(/^\uFEFF/, ''));
    if (backup?.format !== 'finance-ledger' || backup.version !== 1) return { data: null, errors: ['這不是本站支援的完整帳本備份，請使用「完整備份」產生的 JSON。'] };
    const errors = validateLedger(backup.data);
    return { data: errors.length ? null : backup.data, errors };
  } catch { return { data: null, errors: ['JSON 備份檔無法讀取，請確認檔案完整。'] }; }
}
